import { Link } from "react-router-dom";
import TrustLayout, { Section, P, UL } from "../components/TrustLayout";

const versionHistory = [
  { version: "2.1", date: "2 April 2026", notes: "Major expansion — 20 sections total. Added categorised data types, analytics (Tealium), public forums, Do Not Track, enhanced European users section with legal basis and complaints." },
  { version: "2.0", date: "2 April 2026", notes: "Complete revision — restructured into numbered sections with clearer role descriptions, sub-processor transparency, and updated data hosting details." },
  { version: "1.0", date: "25 November 2025", notes: "Initial publication of the Technofocus Privacy Policy." },
];

const privacySections = [
  { id: "priv-intro", label: "1. Introduction" },
  { id: "priv-company", label: "2. Company Information" },
  { id: "priv-role", label: "3. Our Role in Data Processing" },
  { id: "priv-data-collect", label: "4. Personal Data We Collect" },
  { id: "priv-use", label: "5. How We Use Personal Data" },
  { id: "priv-sharing", label: "6. Sharing of Personal Data" },
  { id: "priv-subprocessors", label: "7. Sub-processors" },
  { id: "priv-hosting", label: "8. Data Hosting & Infrastructure" },
  { id: "priv-transfers", label: "9. International Data Transfers" },
  { id: "priv-security", label: "10. Security of Personal Data" },
  { id: "priv-retention", label: "11. Data Retention" },
  { id: "priv-rights", label: "12. Your Rights" },
  { id: "priv-cookies", label: "13. Cookies & Tracking" },
  { id: "priv-analytics", label: "14. Analytics Technologies" },
  { id: "priv-forums", label: "15. Public Forums" },
  { id: "priv-third-party", label: "16. Third-Party Services" },
  { id: "priv-dnt", label: "17. Do Not Track" },
  { id: "priv-updates", label: "18. Updates to This Policy" },
  { id: "priv-contact", label: "19. Contact Us" },
  { id: "priv-eu", label: "20. Notice to European Users" },
];

const dataCategories = [
  {
    label: "Contact & Identity Data",
    items: ["Name", "Email address", "Organization name", "Job role"],
  },
  {
    label: "Account & Authentication Data",
    items: ["Login credentials (securely managed)", "User ID and authentication-related data"],
  },
  {
    label: "Learning & Usage Data",
    items: ["Course enrollment and completion data", "Platform usage and interaction data", "Role-based access data"],
  },
  {
    label: "Event & Transaction Data",
    items: ["Event registration and participation data", "Payment-related information (processed via secure third-party payment providers, where applicable)"],
  },
  {
    label: "Communications Data",
    items: ["Emails, support requests, feedback, and correspondence"],
  },
  {
    label: "Device & Technical Data",
    items: ["IP address", "Device type", "Browser type", "Operating system", "Approximate location"],
  },
];

const sharingCategories = [
  { label: "Customer Organizations", desc: "Employers, partners, or program sponsors" },
  { label: "Program Partners", desc: "Including Microsoft and authorized collaboration platforms for reporting and enablement" },
  { label: "Service Providers (Sub-processors)", desc: "Infrastructure, LMS, labs, analytics, events, and related services" },
  { label: "Affiliates", desc: "Technofocus group entities for operational purposes" },
  { label: "Professional Advisors", desc: "Legal, financial, audit, and compliance advisors" },
  { label: "Authorities", desc: "Law enforcement or regulatory bodies where required" },
  { label: "Business Transfers", desc: "In connection with mergers, acquisitions, restructuring, or asset transfers" },
];

export default function PrivacyPolicy() {
  return (
    <TrustLayout
      title="Privacy Policy"
      lastUpdated="2 April 2026"
      versionHistory={versionHistory}
      sections={privacySections}
      testId="privacy-policy-page"
    >

      {/* 1. Introduction */}
      <Section id="priv-intro" title="Introduction" numbered="1">
        <P>
          <strong>Technofocus Pte. Ltd.</strong> ("Technofocus", "we", "our", or "us") provides learning and enablement platforms, including:
        </P>
        <UL items={[
          "LevelUp Learning Platform",
          "Why Microsoft – Compete Enablement for Partners",
          "Skilling-in-a-Box",
          'Associated training, event, and enablement services (collectively, the "Services")',
        ]} />
        <P>This Privacy Policy explains how we collect, use, process, and protect personal data in connection with our Services.</P>
      </Section>

      {/* 2. Company Information */}
      <Section id="priv-company" title="Company Information" numbered="2">
        <P>Technofocus Pte. Ltd. is the entity responsible for the processing of personal data described in this Privacy Policy.</P>
        <div className="rounded-xl border p-5 mt-3" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <div className="space-y-2 text-sm">
            {[
              { label: "Registered Entity", value: "Technofocus Pte. Ltd." },
              { label: "Registered Address", value: "138 Robinson Road, #26-01 Oxley Tower, Singapore 068906" },
              { label: "Operational Presence", value: "Global, including Singapore and India" },
            ].map(r => (
              <div key={r.label} className="flex gap-3">
                <span className="font-semibold text-gray-700 shrink-0" style={{ minWidth: '180px' }}>{r.label}</span>
                <span className="text-gray-600">{r.value}</span>
              </div>
            ))}
            <div className="flex gap-3">
              <span className="font-semibold text-gray-700 shrink-0" style={{ minWidth: '180px' }}>Contact Email</span>
              <a href="mailto:privacy@technofocus.ai" className="font-medium hover:underline" style={{ color: '#194449' }}>privacy@technofocus.ai</a>
            </div>
          </div>
        </div>
      </Section>

      {/* 3. Role */}
      <Section id="priv-role" title="Our Role in Data Processing" numbered="3">
        <P>In most cases, Technofocus acts as a <strong>data processor</strong> on behalf of customer organizations (such as employers, partners, or program sponsors), which act as the <strong>data controllers</strong>.</P>
        <P>In certain cases (such as direct registrations, communications, or marketing interactions), Technofocus may act as a <strong>data controller</strong>.</P>
      </Section>

      {/* 4. Data Collected */}
      <Section id="priv-data-collect" title="Personal Data We Collect" numbered="4">
        <P>We collect and process personal data necessary to deliver our Services, including:</P>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {dataCategories.map(cat => (
            <div key={cat.label} className="rounded-xl border p-4" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#194449' }}>{cat.label}</p>
              <ul className="space-y-1">
                {cat.items.map(item => (
                  <li key={item} className="flex items-start gap-2 text-xs text-gray-600">
                    <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: '#194449' }}></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border px-5 py-4 flex items-start gap-3" style={{ borderColor: '#D4EC8E', backgroundColor: '#F9FFED' }}>
          <span className="text-sm font-semibold shrink-0" style={{ color: '#07641D' }}>Note:</span>
          <p className="text-sm text-gray-700">We do <strong>not</strong> intentionally collect or process special categories of personal data.</p>
        </div>
      </Section>

      {/* 5. Use */}
      <Section id="priv-use" title="How We Use Personal Data" numbered="5">
        <P>We use personal data to:</P>
        <UL items={[
          "Provide and operate our Services",
          "Manage enrollments and user accounts",
          "Track progress and completion",
          "Enable role-based access to content",
          "Deliver events, certifications, and services",
          "Provide reporting to sponsoring organizations",
          "Improve platform functionality and user experience",
          "Conduct research and development",
          "Communicate service updates, support, and administrative messages",
        ]} />
        <div className="mt-4 rounded-xl border p-4" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-2">Compliance and Protection</p>
          <p className="text-sm text-gray-600 mb-2">We may also use personal data to:</p>
          <UL items={[
            "Comply with legal obligations",
            "Enforce our terms and policies",
            "Protect rights, safety, and security",
            "Detect, prevent, and investigate fraud or misuse",
          ]} />
        </div>
      </Section>

      {/* 6. Sharing */}
      <Section id="priv-sharing" title="Sharing of Personal Data" numbered="6">
        <P>We may share personal data with:</P>
        <div className="space-y-2 mt-3">
          {sharingCategories.map(cat => (
            <div key={cat.label} className="flex gap-3 items-start px-4 py-3 rounded-xl border text-sm" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <span className="font-semibold text-gray-900 shrink-0" style={{ minWidth: '200px' }}>{cat.label}</span>
              <span className="text-gray-600">{cat.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 7. Sub-processors */}
      <Section id="priv-subprocessors" title="Sub-processors" numbered="7">
        <P>Technofocus engages trusted third-party service providers ("Sub-processors") to support the delivery of Services. A current list is available at:</P>
        <Link
          to="/subprocessors"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors mt-1 mb-2"
          style={{ backgroundColor: '#194449' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}
        >
          View Sub-Processor List
        </Link>
      </Section>

      {/* 8. Hosting */}
      <Section id="priv-hosting" title="Data Hosting and Infrastructure" numbered="8">
        <P>Our core platform infrastructure is hosted on <strong>Microsoft Azure</strong> in the United States (West US and East US regions). Certain components (e.g., labs, content delivery, events, and applications) may operate on additional infrastructure providers under Technofocus control. Authentication is centrally managed, while certain modules store role-based access and usage data locally.</P>
      </Section>

      {/* 9. Transfers */}
      <Section id="priv-transfers" title="International Data Transfers" numbered="9">
        <P>Personal data may be transferred to and processed in jurisdictions outside the European Economic Area (EEA), including:</P>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
          {["United States", "Singapore", "India", "United Kingdom"].map(loc => (
            <div key={loc} className="rounded-xl border px-4 py-3 text-center" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <p className="text-xs font-semibold text-gray-800">{loc}</p>
            </div>
          ))}
        </div>
        <P>Such transfers are governed by:</P>
        <UL items={[
          "Standard Contractual Clauses (SCCs)",
          "Contractual safeguards with service providers",
          "Industry-standard security measures",
        ]} />
        <P>Technofocus operates a unified global platform and does not maintain region-specific hosting environments.</P>
      </Section>

      {/* 10. Security */}
      <Section id="priv-security" title="Security of Personal Data" numbered="10">
        <P>We implement appropriate safeguards, including:</P>
        <UL items={[
          "Encryption in transit (TLS)",
          "Encryption at rest (where supported)",
          "Role-based access controls",
          "Least-privilege access",
          "Monitoring and logging",
          "Secure cloud infrastructure",
        ]} />
      </Section>

      {/* 11. Retention */}
      <Section id="priv-retention" title="Data Retention" numbered="11">
        <P>We retain personal data only as long as necessary to deliver Services, fulfill contractual obligations, and comply with legal requirements.</P>
        <div className="mt-3 rounded-xl border overflow-hidden" style={{ borderColor: '#E2EBE9' }}>
          <div className="px-4 py-2.5 border-b text-xs font-bold uppercase tracking-wide" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE', color: '#194449' }}>
            Examples
          </div>
          {[
            { type: "Account data", retention: "Retained while account is active" },
            { type: "Marketing data", retention: "Retained until opt-out" },
            { type: "Support queries", retention: "Retained for a limited period" },
          ].map((r, i) => (
            <div key={r.type} className="flex gap-4 px-4 py-3 text-sm border-t" style={{ borderColor: '#E2EBE9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <span className="font-semibold text-gray-800 shrink-0" style={{ minWidth: '150px' }}>{r.type}</span>
              <span className="text-gray-500">{r.retention}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 12. Rights */}
      <Section id="priv-rights" title="Your Rights" numbered="12">
        <P>Depending on applicable laws, you may have the right to:</P>
        <UL items={[
          "Access your data",
          "Correct inaccuracies",
          "Request deletion",
          "Restrict or object to processing",
          "Request data portability",
        ]} />
        <P>Where Technofocus acts as a processor, requests should be directed to your organization.</P>
      </Section>

      {/* 13. Cookies */}
      <Section id="priv-cookies" title="Cookies and Tracking Technologies" numbered="13">
        <P>We use cookies and similar technologies to enable platform functionality, enhance user experience, and analyze usage patterns.</P>
        <P>
          Detailed information about the cookies we use, including categories, purposes, and how to manage your preferences, is available in our Cookie Policy:{" "}
          <Link to="/cookie-policy" className="underline font-medium" style={{ color: '#194449' }}>
            technofocus.ai/cookie-policy
          </Link>
        </P>
      </Section>

      {/* 14. Analytics */}
      <Section id="priv-analytics" title="Analytics and Tracking Technologies" numbered="14">
        <P>We use analytics tools, including <strong>Tealium</strong>, to understand platform usage and improve performance. These tools may collect:</P>
        <UL items={[
          "Page visits and navigation patterns",
          "Device and browser information",
          "Approximate location (based on IP)",
        ]} />
        <div className="mt-3 rounded-xl border px-5 py-4 flex items-start gap-3" style={{ borderColor: '#D4EC8E', backgroundColor: '#F9FFED' }}>
          <span className="text-sm font-semibold shrink-0" style={{ color: '#07641D' }}>Note:</span>
          <p className="text-sm text-gray-700">Such tools operate <strong>only after user consent</strong> (where required) and data is processed in aggregated or pseudonymized form.</p>
        </div>
      </Section>

      {/* 15. Public Forums */}
      <Section id="priv-forums" title="Public Forums and User Content" numbered="15">
        <P>Information shared in public areas (e.g., forums, discussions) may be visible to others. Users are responsible for any information they choose to disclose.</P>
      </Section>

      {/* 16. Third-party */}
      <Section id="priv-third-party" title="Third-Party Services" numbered="16">
        <P>Our Services may link to or integrate with third-party platforms. These operate under their own privacy policies.</P>
      </Section>

      {/* 17. Do Not Track */}
      <Section id="priv-dnt" title="Do Not Track" numbered="17">
        <P>We do not currently respond to "Do Not Track" browser signals.</P>
      </Section>

      {/* 18. Updates */}
      <Section id="priv-updates" title="Updates to This Policy" numbered="18">
        <P>We may update this Privacy Policy from time to time. Updates will be posted with a revised "Last Updated" date.</P>
      </Section>

      {/* 19. Contact */}
      <Section id="priv-contact" title="Contact Us" numbered="19">
        <P>For questions regarding this Privacy Policy:</P>
        <div className="rounded-xl border p-5 inline-block mt-1 mb-6" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1">Data Protection</p>
          <a href="mailto:privacy@technofocus.ai" className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>
            privacy@technofocus.ai
          </a>
        </div>
        <div className="rounded-xl border p-5" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-3">Additional Resources</p>
          <div className="space-y-2">
            {[
              { label: "Trust Center", href: "/trust" },
              { label: "Data Processing Addendum (DPA)", href: "/dpa" },
              { label: "Sub-Processors", href: "/subprocessors" },
              { label: "Cookie Policy", href: "/cookie-policy" },
            ].map(r => (
              <Link
                key={r.href}
                to={r.href}
                className="flex items-center gap-2 text-sm hover:underline font-medium"
                style={{ color: '#194449' }}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: '#194449' }}></span>
                {r.label}
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* 20. European Users */}
      <Section id="priv-eu" title="Notice to European Users" numbered="20">
        <P>This section applies to users in the European Economic Area (EEA), UK, and Switzerland.</P>

        <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-3">Legal Basis for Processing</h3>
        <P>We process personal data based on:</P>
        <UL items={[
          "Contractual necessity (to provide Services)",
          "Legitimate interests (service improvement, security)",
          "Consent (where required, e.g., cookies, marketing)",
        ]} />

        <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-3">Your Rights</h3>
        <P>You may request:</P>
        <UL items={["Access", "Correction", "Deletion", "Restriction", "Portability", "Objection", "Withdrawal of consent"]} />

        <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-3">Complaints</h3>
        <P>You have the right to lodge a complaint with a supervisory authority.</P>
      </Section>

    </TrustLayout>
  );
}
